// src/components/common/PermissionAwareEditButton.js
import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { canEditCommunityContent } from '../services/communityService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faLock } from '@fortawesome/free-solid-svg-icons';

/**
 * A permission-aware edit button that only shows if the user has edit rights
 * 
 * @param {object} props Component props
 * @param {object} props.item The item to edit (must have owner_id and optionally community_id)
 * @param {function} props.onClick Function to call when button is clicked
 * @param {string} props.variant Button variant (default: "link")
 * @param {string} props.size Button size (default depends on screen size)
 * @param {string} props.color Text color class (default: "text-secondary")
 * @param {boolean} props.showDisabled Whether to show a disabled button when permission is denied (default: false)
 * @param {string} props.title Button tooltip text (default: "Uredi")
 * @param {object} props.icon Custom icon to use instead of the default pencil (e.g., faTrash)
 * @param {string} props.noPermissionTitle Custom text for no permission tooltip (default: "Nimate dovoljenja za urejanje")
 */
const PermissionAwareEditButton = ({ 
  item, 
  onClick, 
  variant = "link",
  color = "text-secondary",
  showDisabled = false,
  title = "Uredi",
  icon = faPencilAlt,
  noPermissionTitle = "Nimate dovoljenja za urejanje",
  ...rest
}) => {
  const { user, userCommunities } = useAuth();
  const isMobile = window.innerWidth < 992;
  
  // Get font size based on screen size
  const fontSize = useMemo(() => {
    return isMobile ? '0.75rem' : '0.9rem';
  }, [isMobile]);

  // Check if user can edit this item
  const canEdit = useMemo(() => {
    // If no item data or no user, can't edit
    if (!item || !user) return false;
    
    // Global admins can edit anything
    if (user.is_global_admin) return true;
    
    // Item owners can edit their own items
    if (user.id === item.owner_id) return true;
    
    // If item is in a community, check community permissions
    if (item.community_id) {
      // Community admins can always edit
      if (user.isAdminOfCommunity && user.isAdminOfCommunity(item.community_id)) {
        return true;
      }
      
      // For regular members, check if the community allows member edits
      const authContext = {
        isGlobalAdmin: user.is_global_admin,
        isAdminOfCommunity: (communityId) => 
          user.isAdminOfCommunity && user.isAdminOfCommunity(communityId),
        userCommunities: userCommunities
        };
      
      return canEditCommunityContent(item.community_id, authContext);
    }
    
    return false;
  }, [item, user]);
  
  // If user doesn't have permission and we don't want to show disabled button, render nothing
  if (!canEdit && !showDisabled) {
    return null;
  }
  
  return (
    <button 
      className={`btn btn-${variant} ${color} p-0 ms-2`}
      onClick={onClick}
      style={{ fontSize }}
      title={canEdit ? title : noPermissionTitle}
      disabled={!canEdit && showDisabled}
      {...rest}
    >
      <FontAwesomeIcon icon={canEdit ? icon : faLock} />
      {!canEdit && showDisabled && (
        <span className="ms-1 small">(No permission)</span>
      )}
    </button>
  );
};

export default PermissionAwareEditButton;

// Example usage:
// <PermissionAwareEditButton 
//   item={event} 
//   onClick={() => setShowEditModal(true)} 
// />
// 
// With custom icon:
// <PermissionAwareEditButton 
//   item={event} 
//   onClick={() => setShowConfirmDelete(true)}
//   icon={faTrash}
//   title="Izbriši dogodek"
//   noPermissionTitle="Nimate dovoljenja za brisanje"
// />